import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faTriangleExclamation } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import EventFeatureFilterEditor, { buildEventFeatureFilter } from '@/components/admin/events/EventFeatureFilterEditor.tsx';
import RecurrenceRuleEditor from '@/components/admin/events/RecurrenceRuleEditor.tsx';
import ReminderMinutesEditor from '@/components/admin/events/ReminderMinutesEditor.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { showErrorToast } from '@/utils/toasts.ts';
import { getUserFacingError } from '@/utils/errorMessages';
import {
  buildEventSeriesUpdate,
  reminderMinutesToRows,
  reminderRowsToMinutes,
  toDateInputValue,
  toTimeInputValue,
  updateRegeneratesOccurrences,
  validateRecurrenceRule
} from '@/utils/event-helpers';
import type { ReminderMinutesRow } from '@/utils/event-helpers';
import type { EventRecurrenceRule, EventSeries, Event, FeatureType } from '@/models';

interface EditEventSeriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  series: EventSeries;
  onSeriesUpdated: (series: EventSeries, events: Event[], regenerated: boolean) => void;
}

export default function EditEventSeriesModal({ isOpen, onClose, series, onSeriesUpdated }: EditEventSeriesModalProps) {
  // Seeded straight from the prop, NOT via the effect below: the payload is built during
  // render, so the very first render already has to see real values instead of empty
  // strings (an empty date field used to crash toRFC3339).
  const [subject, setSubject] = useState(series.subject);
  const [description, setDescription] = useState(series.description);
  const [startDate, setStartDate] = useState(() => toDateInputValue(series.start));
  const [startTime, setStartTime] = useState(() => toTimeInputValue(series.start));
  const [endTime, setEndTime] = useState(() => toTimeInputValue(series.end));
  const [until, setUntil] = useState(series.until);
  const [link, setLink] = useState(series.link);
  const [rule, setRule] = useState<EventRecurrenceRule>(series.rule);
  const [isPublic, setIsPublic] = useState(series.featureFilter === null);
  const [features, setFeatures] = useState<FeatureType[]>(series.featureFilter?.features ?? []);
  const [excludeCommunityMembers, setExcludeCommunityMembers] = useState(series.featureFilter?.excludeCommunityMembers ?? false);
  const [onlyCommunityMembers, setOnlyCommunityMembers] = useState(series.featureFilter?.onlyCommunityMembers ?? false);
  const [reminderRows, setReminderRows] = useState<ReminderMinutesRow[]>(() => reminderMinutesToRows(series.reminderMinutes));
  const [loading, setLoading] = useState(false);

  // Re-seeds a modal instance that a parent keeps mounted across open/close
  // (AdminEventSeriesDetail does), and picks up a series that changed after a save.
  // The initial render is already covered by the useState initialisers above.
  useEffect(() => {
    if (isOpen && series) {
      setSubject(series.subject);
      setDescription(series.description);
      setStartDate(toDateInputValue(series.start));
      setStartTime(toTimeInputValue(series.start));
      setEndTime(toTimeInputValue(series.end));
      setUntil(series.until);
      setLink(series.link);
      setRule(series.rule);
      setIsPublic(series.featureFilter === null);
      setFeatures(series.featureFilter?.features ?? []);
      setExcludeCommunityMembers(series.featureFilter?.excludeCommunityMembers ?? false);
      setOnlyCommunityMembers(series.featureFilter?.onlyCommunityMembers ?? false);
      setReminderRows(reminderMinutesToRows(series.reminderMinutes));
    }
  }, [isOpen, series]);

  // Only fields that genuinely changed go into the payload - see buildEventSeriesUpdate
  // for why sending an unchanged start/end/until/rule would rebuild the whole series.
  const reminderResult = reminderRowsToMinutes(reminderRows);
  const request = buildEventSeriesUpdate({
    subject,
    description,
    link,
    startDate,
    startTime,
    endTime,
    until,
    rule,
    featureFilter: buildEventFeatureFilter(isPublic, features, excludeCommunityMembers, onlyCommunityMembers),
    reminderMinutes: 'minutes' in reminderResult ? reminderResult.minutes : null
  }, series);

  const hasChanges = Object.keys(request).length > 0;
  const willRegenerate = updateRegeneratesOccurrences(request);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim()) {
      showErrorToast('Bitte geben Sie einen Betreff ein');
      return;
    }

    if (!startDate || !startTime || !endTime) {
      showErrorToast('Bitte geben Sie Startdatum, Start- und Endzeit ein');
      return;
    }

    if (!until) {
      showErrorToast('Bitte geben Sie ein Enddatum für die Serie ein');
      return;
    }

    if (!link.trim()) {
      showErrorToast('Bitte geben Sie einen Link ein');
      return;
    }

    if (!isPublic && features.length === 0) {
      showErrorToast('Bitte wählen Sie mindestens ein Feature aus oder markieren Sie die Serie als öffentlich');
      return;
    }

    const ruleError = validateRecurrenceRule(rule);
    if (ruleError !== null) {
      showErrorToast(ruleError);
      return;
    }

    if (new Date(`${startDate}T${endTime}`) <= new Date(`${startDate}T${startTime}`)) {
      showErrorToast('Die Endzeit muss nach der Startzeit liegen');
      return;
    }

    if (until < startDate) {
      showErrorToast('Das Enddatum der Serie darf nicht vor dem Startdatum liegen');
      return;
    }

    if ('error' in reminderResult) {
      showErrorToast(reminderResult.error);
      return;
    }

    setLoading(true);

    try {
      const response = await APIRoutes.updateEventSeries(series.id, request);
      onSeriesUpdated(response.series, response.events, response.regenerated);
      onClose();
    } catch (err) {
      console.error('Error updating event series:', err);
      // The backend reports the occurrence cap and every remaining rule problem as
      // INVALID_INPUT with an English message, so name the likely cause ourselves
      showErrorToast(getUserFacingError(err, 'Fehler beim Aktualisieren der Serie', {
        INVALID_INPUT: 'Die Serie konnte nicht gespeichert werden. Bitte prüfen Sie Rhythmus und Serienende — eine Serie darf höchstens 500 Termine umfassen.'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Event-Serie bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {willRegenerate && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 dark:text-yellow-400 shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Zukünftige Termine werden neu aufgebaut
                  </h3>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    Sie haben Start, Uhrzeit, Serienende oder den Rhythmus geändert. Alle zukünftigen Termine der Serie werden gelöscht und neu erzeugt — einzeln angepasste Termine verlieren dabei ihre Anpassungen. Vergangene Termine bleiben unverändert.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <label htmlFor="editSeriesSubject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Betreff *
            </label>
            <input
              type="text"
              id="editSeriesSubject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="editSeriesDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung
            </label>
            <textarea
              id="editSeriesDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
            />
          </div>

          {/* Start date + time window */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="editSeriesStartDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Serienstart *
              </label>
              <input
                type="date"
                id="editSeriesStartDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="editSeriesStartTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Uhrzeit *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  id="editSeriesStartTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">bis</span>
                <input
                  type="time"
                  id="editSeriesEndTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Until */}
          <div>
            <label htmlFor="editSeriesUntil" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Serienende *
            </label>
            <input
              type="date"
              id="editSeriesUntil"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Letzter Tag, an dem noch ein Termin erzeugt wird (einschließlich).
            </p>
          </div>

          {/* Link */}
          <div>
            <label htmlFor="editSeriesLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link *
            </label>
            <input
              type="url"
              id="editSeriesLink"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://..."
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Änderungen am Link werden auf alle zukünftigen, nicht einzeln angepassten Termine übertragen.
            </p>
          </div>

          <RecurrenceRuleEditor rule={rule} onChange={setRule} until={until} disabled={loading} />

          <EventFeatureFilterEditor
            isPublic={isPublic}
            onIsPublicChange={setIsPublic}
            features={features}
            onFeaturesChange={setFeatures}
            excludeCommunityMembers={excludeCommunityMembers}
            onExcludeCommunityMembersChange={setExcludeCommunityMembers}
            onlyCommunityMembers={onlyCommunityMembers}
            onOnlyCommunityMembersChange={setOnlyCommunityMembers}
            disabled={loading}
            entityLabel="Serie"
          />

          <ReminderMinutesEditor rows={reminderRows} onChange={setReminderRows} disabled={loading} />
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
            Änderungen an den Erinnerungen werden auf alle zukünftigen, nicht einzeln angepassten Termine übertragen.
          </p>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !subject.trim() || !startDate || !startTime || !endTime || !until || !link.trim() || (!isPublic && features.length === 0) || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'Änderungen speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
